import React, { useEffect, useState } from 'react';
import { documentNumberLabel } from './documentLabels';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, Chip, CircularProgress, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TablePagination, TableRow, Typography,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { QuotationService } from '../../services/quotation.service';
import { Quotation, QUOTATION_STATUS_COLORS } from '../../interfaces/sales/quotation.interface';
import { useToast } from '../../context/ToastContext';
import { AuthService } from '../../services/auth.service';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { formatMoney, humanizeEnum } from '../../utils/format';
import { formatDate } from '../../utils/dateUtils';
import SalespersonSelect, { salespersonParams } from './scope/SalespersonSelect';
import { formatFc, isForeignCurrency } from '../../utils/fcDocuments';   // G5b

const QuotationList: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const canManage = usePermission(PERMISSIONS.QUOTATION_MANAGE);
  const [rows, setRows] = useState<Quotation[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [loading, setLoading] = useState(true);
  const [owner, setOwner] = useState<string | null>(null);

  useEffect(() => { load(); }, [page, size, owner]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = async () => {
    const institutionId = AuthService.getSelectedInstitution();
    try {
      setLoading(true);
      const res = await QuotationService.listByInstitution(institutionId, page, size, salespersonParams(owner));
      setRows(res.content);
      setTotal(res.totalElements);
    } catch (e) {
      toast.showApiError(e, 'Failed to load quotations');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }} data-help-page="sales.quotations.page">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5">Quotations</Typography>
        {canManage && (
          <Button data-help="sales.quotations.new" variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/sales/quotations/new')}>
            New Quotation
          </Button>
        )}
      </Box>
      <Box data-help-section="sales.quotations.filter" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <SalespersonSelect data-help="sales.quotations.filter.salesperson"
          withUnassigned sx={{ flex: '1 1 220px', maxWidth: 320 }} value={owner}
          onChange={id => { setOwner(id); setPage(0); }}
        />
      </Box>
      {loading ? <CircularProgress /> : (
        <TableContainer component={Paper} data-help-section="sales.quotations.table">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell data-help="sales.quotations.col.number">Quotation #</TableCell>
                <TableCell data-help="sales.quotations.col.customer">Customer</TableCell>
                <TableCell data-help="sales.quotations.col.salesperson">Salesperson</TableCell>
                <TableCell data-help="sales.quotations.col.date">Date</TableCell>
                <TableCell data-help="sales.quotations.col.validUntil">Valid Until</TableCell>
                <TableCell align="right" data-help="sales.quotations.col.total">Total</TableCell>
                <TableCell data-help="sales.quotations.col.status">Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(row => (
                <TableRow
                  key={row.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/sales/quotations/${row.id}`)}
                >
                  <TableCell>{documentNumberLabel(row.quotationNumber)}</TableCell>
                  <TableCell>{row.customerName ?? '—'}</TableCell>
                  <TableCell>{row.salespersonName ?? 'Unassigned'}</TableCell>
                  <TableCell>{formatDate(row.quotationDate)}</TableCell>
                  <TableCell>{row.validUntil ? formatDate(row.validUntil) : '—'}</TableCell>
                  <TableCell align="right">{formatMoney(row.totalAmount)}{isForeignCurrency(row.currencyCode) && <Typography variant="caption" color="text.secondary" display="block">{formatFc(row.currencyCode!, row.foreignTotal)}</Typography>}</TableCell>
                  <TableCell>
                    <Chip
                      label={humanizeEnum(row.status)}
                      color={QUOTATION_STATUS_COLORS[row.status]}
                      size="small"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={size}
            onRowsPerPageChange={(e) => { setSize(Number(e.target.value)); setPage(0); }}
          />
        </TableContainer>
      )}
    </Box>
  );
};

export default QuotationList;
